import React from 'react';
import './CampaignSkeleton.css';

/*
 * The campaigns' loading pieces, after the knowledge base's (SL-1909): bones
 * drawn where the content will land, at its size, so nothing moves when it
 * arrives. Plain spans rather than AntD's `Skeleton`: AntD's shimmer ignores
 * `prefers-reduced-motion`, and its paragraph block has one shape — rows of
 * text — where these screens have tables, cards, chips and charts.
 *
 * Each skeleton is a block at the content's height with a gradient sweep.
 */

type Size = number | string;

const px = (size: Size | undefined) => (typeof size === 'number' ? `${size}px` : size);

export interface BoneProps {
  /** Width of the bone; a number is pixels. Default: the whole line. */
  width?: Size;
  /** Height of the bone; a number is pixels. Default: one 12px line of text. */
  height?: Size;
  /** `pill` for chips and tags, `circle` for icons and dots. */
  shape?: 'line' | 'pill' | 'circle';
  className?: string;
}

/** One grey block. Empty and silent: the status around it does the talking. */
export const Bone: React.FC<BoneProps> = ({ width, height, shape = 'line', className }) => (
  <span
    className={[
      'campaign-skeleton__bone',
      shape !== 'line' && `campaign-skeleton__bone--${shape}`,
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={{ width: px(width), height: px(height) }}
    aria-hidden="true"
  />
);

/**
 * One line of text: a bone the height of the font, centred in a box the height
 * of the line — so a column of these measures what the text will.
 */
export const TextBone: React.FC<{ width?: Size; size?: Size; lineHeight?: Size }> = ({
  width,
  size = 14,
  lineHeight = 22,
}) => (
  <span className="campaign-skeleton__text" style={{ height: px(lineHeight), width: px(width) }}>
    <Bone height={size} />
  </span>
);

export interface SkeletonStatusProps {
  /** What is loading, read by assistive tech and never drawn. */
  label: string;
  className?: string;
  /** Tests find the region by it. */
  testId?: string;
  children: React.ReactNode;
  /** The element to render: a `section` keeps a card's semantics. Default `div`. */
  as?: 'div' | 'section';
}

/**
 * A region that is loading. `role="status"` with its text inside, visually
 * hidden: a label alone on an empty live region is often never read. No
 * `aria-busy` — on a live region it tells assistive tech to hold the
 * announcement (the same call SL-1909 made).
 */
export const SkeletonStatus: React.FC<SkeletonStatusProps> = ({
  label,
  className,
  testId,
  children,
  as: Tag = 'div',
}) => (
  <Tag className={className} role="status" data-testid={testId}>
    <span className="campaign-skeleton__sr-only">{label}</span>
    {children}
  </Tag>
);
